import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from "../utils/session";
import { API_BASE_URL } from '../../config';

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

export default function MembersTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const navigate = useNavigate();
  const role = Session.getRole();
  const isTeamLeader = role === "Team Leader";
  const isSuperAdmin = role === "Super Admin";

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(
          `${API_BASE_URL}/api/Members/Users_List`,
          {
            headers: authHeaders()
          }
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        const normalized = Array.isArray(data)
          ? data.map((u) => ({
            ...u,
            UserId: u.userId ?? u.UserId,
            UserName: u.userName ?? u.UserName,
            FirstName: u.firstName ?? u.FirstName,
            LastName: u.lastName ?? u.LastName,
            Email: u.email ?? u.Email,
            PhoneNo: u.phoneNo ?? u.PhoneNo,
            MemberResingOn: u.MemberResingOn ?? u.MemberResingOn,
            UserRoleId: u.userRoleId ?? u.UserRoleId,
            CompaniesId: u.companiesId ?? u.CompaniesId,
            IsActive: u.isActive ?? u.IsActive ?? u.active ?? u.Active,
          }))
          : [];
        setRows(normalized);
      } catch (err) {
        console.error('Error loading users:', err);
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/Members/delete/${userId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (!response.ok) {
        navigate('/Members');
      }
      setRows((prev) => prev.filter((u) => u.UserId !== userId));
      setSuccessMsg('Member deleted successfully');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleResign = async (userId) => {
    if (!window.confirm('Are you sure this member is resigning?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/Members/Resign/${userId}`, {
        method: 'PATCH',
        headers: authHeaders()
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const resignDate = new Date().toISOString();

      setRows(prev =>
        prev.map(u => u.UserId === userId ? { ...u, MemberResignedOn: resignDate } : u)
      );

      setSuccessMsg('Member resigned successfully');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err) {
      alert(err.message || 'Failed to mark member as resigned');
    }
  };



  if (loading) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-muted">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-responsive">
        <div className="p-3 text-center text-danger">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      {successMsg && (
        <div
          className="alert alert-success text-center"
          style={{ marginBottom: '10px' }}
        >
          {successMsg}
        </div>
      )}

      <table className="table table-custom table-hover">
        <thead>
          <tr>
            <th>Roll</th>
            <th>Username</th>
            <th>Full name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role Name</th>
            <th>Active</th>
            {!isTeamLeader && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-muted py-3">
                No users found.
              </td>
            </tr>
          )}
          {rows.map((r, index) => (
            <tr key={r.UserId}>
              <td>{index + 1}</td>
              <td>
                <div className="employee-info">
                  <img
                    src={`/images/${r.UserName}.png`} 
                    className="avatar"
                    alt={r.UserName}
                    style={{ width: 40, height: 40, borderRadius: '50%' }}
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = `https://i.pravatar.cc/100?u=${r.UserId || r.UserName}`; 
                    }}
                  />
                  <div className="emp-text">
                    <h6>{r.UserName}</h6>
                  </div>
                </div>
              </td>
              <td>{`${r.FirstName || ''} ${r.LastName || ''}`.trim()}</td>
              <td>{r.Email}</td>
              <td>{r.PhoneNo || ''}</td>
              <td>{r.roleName}</td>
              <td>{(r.IsActive ?? r.Active) ? 'Yes' : 'No'}</td>
              {!isTeamLeader && (
                <td>
                  <i
                    className="fas fa-edit text me-2"
                    style={{ cursor: 'pointer', color: '#0084fe' }}
                    onClick={() =>
                      navigate(`/Member/Edit/${r.UserId}`, { state: { user: r } })
                    }
                  />
                  <i
                    className="fas fa-trash text"
                    title="Delete"
                    style={{ cursor: 'pointer', color: '#0084fe' }}
                    onClick={() => handleDelete(r.UserId)}
                  />
                  {isSuperAdmin && (
                    <button
                      title="Resign"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        color: '#0084fe',
                        border: '1px solid #0084fe',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        marginLeft: '5px'
                      }}
                      onClick={() => handleResign(r.UserId)}
                    >
                      <i className="fas fa-user-times me-1"></i>
                      Resign
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
